import i18n from 'i18next';
import {initReactI18next} from 'react-i18next';
import HTTPApi from 'i18next-http-backend';
import moment from "moment";
import {PWA_DOMAIN_DEV, PWA_DOMAIN_PROD, LOCKER_BANK, HH_mm_ss, REGEXP_DOMAIN, REGEXP_LB_ID, ASSET_RENTAL_USE_CASE, LUGGAGE_USE_CASE, PARCEL_USE_CASE, ASSET_RENTAL_PAGE_FLOW, DYNAMIC_PAGE_FLOW, LUGGAGE_PAGE_FLOW, PARCEL_PAGE_FLOW, HIVEBOARD, DUROLT, BLUEDART, BLUEDART_PAGE_FLOW, SUCCESS, REGEXP_DOT, BASE64_URL_CHARS_REGEX, MIN_ENCRYPTED_LENGTH, IMPORT_EXPORT_USE_CASE, currencies} from "../assets/constants/Constants";
import {DEFAULT_LANGUAGE_ENGLISH, LANGUAGE_LABELS, LANGUAGE_MASTER, LANGUAGE_OPTIONS} from "../assets/constants/LanguageOptions";
import ShowToast from "../components/ToastComponent";
import { ACCESS_TOKEN, ACCESS_TOKEN_EXPIRY_TIME, IS_LOGGED_IN, LOCAL_STORAGE, LS_DOMAIN_AND_LOCKER_BANK, LS_TENANT_INFO, LS_LOCKER_BANK_INFO, LS_TENANT_ASSIGNMENT_LOCKER_BANK_SETTINGS, OPERATION_ID, REFRESH_TOKEN, REFRESH_TOKEN_EXPIRY_TIME, TXN_ID, USER_INFO, LS_CUSTOMER, LS_UNIQUE_DEVICE_ID, LS_PAGE_FLOW, PAYMENT_REFERENCE_ID, PAYMENT_GATEWAY_REFERENCE, LS_ALLOWED_LANGUAGE, LS_SELECTED_LANGUAGE, LS_LOCAL_LANGUAGE, SESSION_STORAGE, SS_PAYMENT_GATEWAY, SS_PAYMENT_GATEWAY_ACCESS_KEY, ADVANCE_BOOKING, BULK_BOOKING } from "../assets/constants/BrowserStorageKeys";
import { getSecureItemFromSpecificStorage, removeSecureItemFromSpecificStorage, storeSecureItemInSpecifiedStorage } from "./BrowserStorageAccessMiddleware";
import { buildBulkPaymentInitial, getAssignedLockerList, getAvailableLockerSizesWithUnits, reserveLocker } from '../api/api';
import { HOME, LOCKER_SIZE_SELECTION, LOCKER_UNIT_SELECTION, OPEN_LOCKER, PARCEL_OWNER, PAYMENT_GATEWAY, VERIFY_OTP } from '../assets/constants/PageList';
import CryptoJS from "crypto-js";
import React from "react";
import { LockerIcon } from '../assets/constants/Icons';
import { addMinutes, format, isToday, startOfDay } from 'date-fns';


export const devConsoleLog = (...args) => {
    if (process.env.REACT_APP_ENV !== 'prod') {
        console.log(...args);
    }
};

export const initializeI18nAndThenAppendCustomerSpecificStylesheet = () => {
    const customerNameLowerCase = getSecureItemFromSpecificStorage(LOCAL_STORAGE, LS_CUSTOMER) ? getSecureItemFromSpecificStorage(LOCAL_STORAGE, LS_CUSTOMER)?.toLowerCase() : DUROLT.toLowerCase();
    const allowd_language = getSecureItemFromSpecificStorage(LOCAL_STORAGE,LS_ALLOWED_LANGUAGE) ?  getSecureItemFromSpecificStorage(LOCAL_STORAGE,LS_ALLOWED_LANGUAGE) : [];
    i18n
        .use(initReactI18next)
        .use(HTTPApi)
        .init({
            lng: retrieveISOCodeOfPreviouslySelectedLanguageFromLocalStorage(),
            fallbackLng: DEFAULT_LANGUAGE_ENGLISH.value,
            backend: {
                loadPath: loadPathWithNamespace,
                allowMultiLoading: true,
            },
            interpolation: {
                escapeValue: false
            },
            ns: [customerNameLowerCase, 'translation'],
            defaultNS: customerNameLowerCase,
            fallbackNS: 'translation',
            preload: [DEFAULT_LANGUAGE_ENGLISH.value, ...allowd_language],
            returnObjects: true
        })
        .then(appendCustomerSpecificStylesheet);
};

const loadPathWithNamespace = (lng, namespace) => {
    let path = `/assets/locales/${lng}/translation.json`;
    const customerNameLowerCase = getSecureItemFromSpecificStorage(LOCAL_STORAGE, LS_CUSTOMER)?.toLowerCase();

    if (namespace[0] === customerNameLowerCase) {
        path = `https://pwadev.s3.ap-south-1.amazonaws.com/brand/${customerNameLowerCase}/${customerNameLowerCase}.json`;
    }

    // console.log('path: ', path);

    return path;
};

const updateI18nResourcesAndCustomerStylesheet = async (newCustomer) => {
    await updateI18nResources(newCustomer);
    appendCustomerSpecificStylesheet();
}

const updateI18nResources = async (newCustomer) => {
    const customerNameLowerCase = newCustomer.toLowerCase();

    await i18n.loadNamespaces(customerNameLowerCase);
    i18n.setDefaultNamespace(customerNameLowerCase);
    await i18n.reloadResources(i18n.language, [customerNameLowerCase]);
};

export const appendCustomerSpecificStylesheet = () => {
    const customer = getSecureItemFromSpecificStorage(LOCAL_STORAGE, LS_CUSTOMER);
    devConsoleLog('customer selected: ', customer);
    const documentHead = document.head;
    const existingStylesheet = document.getElementById('customer-stylesheet');

    if (existingStylesheet && ( !existingStylesheet.className.includes(customer?.toLowerCase()) )) {
        documentHead.removeChild(existingStylesheet);
    }

    const stylesheet = document.createElement('link');
    stylesheet.type = 'text/css';
    stylesheet.rel = 'stylesheet';
    stylesheet.id = 'customer-stylesheet';
    stylesheet.className = `${customer ? customer.toLowerCase() : DUROLT.toLowerCase()}-stylesheet`;
    stylesheet.href = i18n.t('css') !== 'css' ? i18n.t('css') : '/assets/css/durolt.css';
    // console.log('css link: ', i18n.t('css'));
    // console.log('stylesheet href: ', stylesheet.href);
    devConsoleLog('stylesheet: ', stylesheet);
    documentHead.appendChild(stylesheet);
}

export const getDomainAndLockerBankInfoFromScannedQRCode = (qrCodeData) => {
    const luggagePWALinkPattern = new RegExp(`(${PWA_DOMAIN_DEV}|${PWA_DOMAIN_PROD})/${LOCKER_BANK}/.*`);

    if (luggagePWALinkPattern.test(qrCodeData)) {
        const domainAndLockerBankInfoInBase64 = qrCodeData.split(`/${LOCKER_BANK}/`)[1];
        const domainAndLockerBankInformation = window.atob(domainAndLockerBankInfoInBase64);
        const isDomainValid = /^{.*(?="domain"\s*:\s*"[^"]{1,}").*}$/.test(domainAndLockerBankInformation);
        const isLockerBankIdValid = /^{.*(?="lockerBankId"\s*:\s*"\d+").*}$/.test(domainAndLockerBankInformation);

        if (isDomainValid && isLockerBankIdValid) {
            return JSON.parse(domainAndLockerBankInformation);
        }
    }

    return null;
}

export const checkIfAssignmentIsProperlyConfigured = async (tenantLockerBankAndAssignmentInfo, selectedAssignmentIndex = 0) => {
    const tenantInfo = tenantLockerBankAndAssignmentInfo.tenant_settings;
    const lockerBankInfo = tenantLockerBankAndAssignmentInfo.locker_bank;
    const assignment = tenantLockerBankAndAssignmentInfo.locker_assignments[selectedAssignmentIndex];
    const isAssignmentActive = assignment?.is_active;
    const isPwaEnabled = assignment?.usecase_settings?.pwa?.value;
    const isAnyUserAuthenticationMethodAvailable = isAnyAuthenticationMethodAvailable(assignment?.usecase_settings);
    const isGeoFencingEnabled = assignment?.is_geofencing;
    const areBothLockerBankCoordinatesAvailable = (parseInt(lockerBankInfo?.latitude) % 1 === 0) && (parseInt(lockerBankInfo?.longitude) % 1 === 0);
    // const isPwaRuleEngineAvailable = (assignment?.pwa_rule_engines.length > 0) && (assignment?.pwa_rule_engines[0]?.flow.length > 0);

    if ( !isAssignmentActive ) {
        return { isConfigured: false, errorMessage: 'inactiveAssignment' };
    }
    if ( !isPwaEnabled ) {
        return { isConfigured: false, errorMessage: 'pwaAppDisabled' };
    }
    if ( !isAnyUserAuthenticationMethodAvailable ) {
        return { isConfigured: false, errorMessage: 'noAuthOptionsAvailable' };
    }
    if ( isGeoFencingEnabled && !areBothLockerBankCoordinatesAvailable ) {
        return { isConfigured: false, errorMessage: 'Geofencing is enabled but locker bank location information is unavailable. Please contact admin.' };
    }
    // else if ( !isPwaRuleEngineAvailable ) {
    //     return { isConfigured: false, errorMessage: 'Rule Engine not available. Please contact admin.'};
    // }

    storeTenantInfoInLocalStorge(tenantInfo);
    storeLockerBankInfoInLocalStorge(lockerBankInfo);
    storeSecureItemInSpecifiedStorage(LOCAL_STORAGE, LS_TENANT_ASSIGNMENT_LOCKER_BANK_SETTINGS, assignment);
    // storeSecureItemInSpecifiedStorage(LOCAL_STORAGE, LS_PAGE_FLOW, assignment?.pwa_rule_engines[0]?.flow);
    await storeCustomerInLocalStorageAndUpdateResourcesForCurrentUseCase(assignment);

    return true;
}

export const storeTenantInfoInLocalStorge = (tenantInfo) => {
    const tenantInformation = {
        name: tenantInfo.name
    };

    storeSecureItemInSpecifiedStorage(LOCAL_STORAGE, LS_TENANT_INFO, tenantInformation);
};

export const storeLockerBankInfoInLocalStorge = (lockerBankInfo) => {
    const lockerBankInformation = {
        name: lockerBankInfo.name,
        id: lockerBankInfo.id,
        latitude: lockerBankInfo.latitude,
        longitude: lockerBankInfo.longitude,
        timezone: lockerBankInfo.timezone
    };

    storeSecureItemInSpecifiedStorage(LOCAL_STORAGE, LS_LOCKER_BANK_INFO, lockerBankInformation);
};

export const storeCustomerInLocalStorageAndUpdateResourcesForCurrentUseCase = async (assignment) => {
    const isShowDropButtonEnabled = assignment?.usecase_settings?.show_pickup_btn_pwa;
    const isShowPickupButtonEnabled = assignment?.usecase_settings?.show_drop_btn_pwa;
    if (assignment?.usecase === ASSET_RENTAL_USE_CASE) {
        storeSecureItemInSpecifiedStorage(LOCAL_STORAGE, LS_PAGE_FLOW, ASSET_RENTAL_PAGE_FLOW);
        storeSecureItemInSpecifiedStorage(LOCAL_STORAGE, LS_CUSTOMER, HIVEBOARD);
        await updateI18nResourcesAndCustomerStylesheet(HIVEBOARD);
    }
    else if (assignment?.usecase === LUGGAGE_USE_CASE) {
        storeSecureItemInSpecifiedStorage(LOCAL_STORAGE, LS_PAGE_FLOW, LUGGAGE_PAGE_FLOW);
        storeSecureItemInSpecifiedStorage(LOCAL_STORAGE, LS_CUSTOMER, DUROLT);
        await updateI18nResourcesAndCustomerStylesheet(DUROLT);
    }
    else if (assignment?.usecase === PARCEL_USE_CASE || assignment?.usecase === IMPORT_EXPORT_USE_CASE) {
        storeSecureItemInSpecifiedStorage(LOCAL_STORAGE, LS_PAGE_FLOW, PARCEL_PAGE_FLOW);
        if(isShowDropButtonEnabled || isShowPickupButtonEnabled) {
            storeSecureItemInSpecifiedStorage(LOCAL_STORAGE, LS_CUSTOMER, DUROLT);
            await updateI18nResourcesAndCustomerStylesheet(DUROLT);
            return;
        }
        storeSecureItemInSpecifiedStorage(LOCAL_STORAGE, LS_CUSTOMER, BLUEDART);
        await updateI18nResourcesAndCustomerStylesheet(BLUEDART);
    }
    else {
        storeSecureItemInSpecifiedStorage(LOCAL_STORAGE, LS_PAGE_FLOW, DYNAMIC_PAGE_FLOW);
        storeSecureItemInSpecifiedStorage(LOCAL_STORAGE, LS_CUSTOMER, DUROLT);
        await updateI18nResourcesAndCustomerStylesheet(DUROLT);
    }

    return;
}

export const getPageNameUsingPath = (path) => getSecureItemFromSpecificStorage(LOCAL_STORAGE, LS_PAGE_FLOW)?.find((page) => path === page.path)?.name;

export const getPathForNextPage = (currentPageName, nextPageName) => {
    const pageFlow = getSecureItemFromSpecificStorage(LOCAL_STORAGE, LS_PAGE_FLOW);
    if (!pageFlow) return;

    // First find method returns the current page object
    // Second find method uses the exit pages of current page and returns the path for next page
    return pageFlow
        .find((page) => currentPageName === page.name)
        ?.exitPages?.find((exitPage) => nextPageName === exitPage.key)?.path;
}

export const redirectToNextPageWithStateObject = (history, redirectionMethod, currentPageName, nextPageName, stateObject = null) => {
    const redirectionPathname = getPathForNextPage(currentPageName, nextPageName);

    if (redirectionPathname) {
        const redirectionObject = {
            pathname: redirectionPathname
        };

        if (stateObject) {
            redirectionObject.state = stateObject;
        }
        if (redirectionMethod === 'push') {
            history.push(redirectionObject);
        }
        else if (redirectionMethod === 'replace') {
            history.replace(redirectionObject);
        }
        else {
            console.error('Redirection error: Redirection method not specified');
            ShowToast('Error during redirection. Please contact admin', 'ERROR');
        }
    }
    else {
        console.error(`Redirection error: Couldn't get redirection pathname for following current and next pages: ${currentPageName} to ${nextPageName}`);
        ShowToast('Error during redirection. Please contact admin', 'ERROR');
    }
};

export const retrieveDetailsOfPreviouslySelectedLanguageFromLocalStorage = () => {
    let previouslySelectedLanguage = DEFAULT_LANGUAGE_ENGLISH;

    if (localStorage.getItem("selectedLanguage")) {
        previouslySelectedLanguage = JSON.parse(localStorage.getItem("selectedLanguage"));
    }

    return previouslySelectedLanguage;
}

export const retrieveISOCodeOfPreviouslySelectedLanguageFromLocalStorage = () => {
    let previouslySelectedLanguage = DEFAULT_LANGUAGE_ENGLISH;

    if (localStorage.getItem("selectedLanguage")) {
        previouslySelectedLanguage = JSON.parse(localStorage.getItem("selectedLanguage"));
    }
       
    return previouslySelectedLanguage.value;
}

export const convertMillisecondsToSpecifiedTimeFormat = (milliseconds, timeFormat) => {
    const duration = moment.duration(milliseconds);
    const hours = Math.floor(duration.asHours());
    const minutes = moment.utc(milliseconds).format("mm");

    if (timeFormat === HH_mm_ss) {
        const seconds = moment.utc(milliseconds).format("ss");
        return `${hours}:${minutes}:${seconds}`;
    }

    return `${hours}:${minutes}`;
}

// export const convertDurationStringToMilliseconds = (durationString) => {
//     // input formats
//     // with days: 1 day, 2 hours, 5 minutes, 15 seconds: "1 02:05:15"
//     // without days: 23 hours, 59 minutes, 59 seconds: "23:59:59"
//     console.log("durationString: ", durationString);
//     let days = 0;
//     let timePart = "";

//     if (/\d\s\d{2}:\d{2}:\d{2}/.test(durationString)) {
//         [days, timePart] = durationString.split(' ');
//     }
//     else {
//         durationString.length>0 ?
//         timePart = durationString : timePart = "06:00:00";
//     }

//     const [hours, minutes, seconds] = timePart.split(':').map(Number);
//     const totalSeconds = (parseInt(days, 10) * 24 * 3600) + (hours * 3600) + (minutes * 60) + seconds;

//     return totalSeconds * 1000;
// };
export const convertDurationStringToMilliseconds = (durationString) => {
  // ✅ Handle undefined / null / empty
  if (!durationString) {
    durationString = "06:00:00";
  }

  console.log("durationString: ", durationString);

  let days = 0;
  let timePart = "";

  // Format: "1 02:05:15"
  if (/\d+\s\d{2}:\d{2}:\d{2}/.test(durationString)) {
    [days, timePart] = durationString.split(" ");
  } 
  // Format: "02:05:15"
  else {
    timePart = durationString;
  }

  const [hours = 0, minutes = 0, seconds = 0] =
    timePart.split(":").map(Number);

  const totalSeconds =
    (parseInt(days, 10) || 0) * 24 * 3600 +
    hours * 3600 +
    minutes * 60 +
    seconds;

  return totalSeconds * 1000;
};
export const getFormatTime=(date)=>{
    let Sort= date.toString().replace(/T/, ' ').replace(/\..+/, '').split(" ")   
   var time =Sort[1].toString()
  
      function tConvert (time) {
       // Check correct time format and split into components
          time = time.toString ().match (/^([01]\d|2[0-3])(:)([0-5]\d)(:[0-5]\d)?$/) || [time];
       if (time.length > 1) { 
         time = time.slice (1);
        time[5] = +time[0] < 12 ? 'AM' : 'PM'; 
         time[0] = +time[0] % 12 || 12; 
           time.splice(3, 1," "); 
       }
         return time.join (''); 
      }
       return tConvert(time)
  } 

  export const getFormatDate=(date)=>{
   let Sort= date.toString().replace(/T/, ' ').replace(/\..+/, '').split(" ")   
   const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
   const d = new Date(Sort[0]);
    let month = months[d.getMonth()];
    let year= d.getFullYear();
    let date2=d.getDate()
   return date2+" "+month+" "+year
 } 


 export const isValidDomainAndLockerBankId = (domainAndLockerBankInfoInBase64) => {
    let domainAndLockerBankInformation = null;
    let isDomainValid = null;
    let isLockerBankIdValid = null;

    try {
        domainAndLockerBankInformation = window.atob(domainAndLockerBankInfoInBase64);
        isDomainValid = REGEXP_DOMAIN.test(domainAndLockerBankInformation);
        isLockerBankIdValid = REGEXP_LB_ID.test(domainAndLockerBankInformation);
        if(isDomainValid && isLockerBankIdValid){
            return true
        }
        return;
    }
    catch (error) {
        console.log('Error while scanning the QR code: ', error);
        return false
    }
 }

export const removeSpacesAndHyphens = (input) =>  {
    return input.replace(/[\s-]/g, '');
}

export const extractDomain = (url) => {
    const regex = /https:\/\/([^\/]+)\//;
    const match = url.match(regex);
    return match ? match[1] : null;
};

export const convertSecondsToDateTimeFormat = (expiryIn) => {
    const currentTime = moment(); // Current time
    const accessExpiresAt = currentTime.clone().add(expiryIn, 'seconds');
    return accessExpiresAt;
}

export const extractPageNumber = (url) => {
    const match = url.match(/[?&]page=(\d+)/);
    return match ? parseInt(match[1], 10) : null;
};

export const clearLocalStorageForUser = (customer) => {
    if (customer === HIVEBOARD) {
        const arrayOfItemsToRemoveFromLocalStorage = [
            USER_INFO,
            ACCESS_TOKEN,
            REFRESH_TOKEN,
            ACCESS_TOKEN_EXPIRY_TIME,
            REFRESH_TOKEN_EXPIRY_TIME,
            TXN_ID,
            PAYMENT_REFERENCE_ID,
            PAYMENT_GATEWAY_REFERENCE,
            OPERATION_ID,
            IS_LOGGED_IN
        ];

        arrayOfItemsToRemoveFromLocalStorage.forEach((item) => {
            removeSecureItemFromSpecificStorage(LOCAL_STORAGE, item);
        });
    }
    else {
        const listOfItemsInLocalStorage = Object.keys(localStorage);
        const listOfItemsInLocalStorageToKeep = [LS_UNIQUE_DEVICE_ID];

        listOfItemsInLocalStorage.forEach((itemKey) => {
            if ( !(listOfItemsInLocalStorageToKeep.includes(itemKey)) ) {
                removeSecureItemFromSpecificStorage(LOCAL_STORAGE, itemKey);
            }
        });
    }
}

export const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
};

export const isAnyAuthenticationMethodAvailable = (settings) => {
    if (settings?.verify_with_mobile_otp_pwa?.value) {
        return true;
    }
    else if (settings?.verify_with_email_otp_pwa?.value) {
        return true;
    }
    else if (settings?.login_with_google_pwa?.value) {
        return true;
    }
    else if (settings?.login_with_facebook_pwa?.value) {
        return true;
    }
    else if (settings?.link_my_device?.value) {
        return true;
    }else if (settings?.allow_anonymous_drop_pwa?.value && settings?.show_drop_btn_pwa) {
        return true;
    }else if (settings?.show_drop_btn_pwa) {
        return true;
    }else if(settings?.show_pickup_btn_pwa){
        return true;
    }

    return false;
}

export const hasExtraAuthMethods = (settings) => {
    let isExtraAuthMethodsAvailable = false;

    if (settings?.verify_with_mobile_otp_pwa?.value && settings?.verify_with_email_otp_pwa?.value && settings?.link_my_device?.value) {
        isExtraAuthMethodsAvailable = true;
    }
    return isExtraAuthMethodsAvailable;
}

export const extractBase64FromUrl = (url) =>  {
    try {
        const urlObj = new URL(url);
        const pathSegments = urlObj.pathname.split('/').filter(segment => segment); // Filter out empty segments
        return pathSegments[pathSegments.length - 1];
    } catch (error) {
        console.error('Invalid URL:', error);
        return null;
    }
}

export const setDomainAndLockerBankIdInLocalStorage = (domainAndLockerBankInfoObject) => {
    let domain = domainAndLockerBankInfoObject?.domain;
    const baseUrl = domain.includes('https://') || domain.includes('http://')
      ? `${domain}`
      : `https://${domain}/`;
    domainAndLockerBankInfoObject.domain = baseUrl;
    storeSecureItemInSpecifiedStorage(LOCAL_STORAGE, LS_DOMAIN_AND_LOCKER_BANK, domainAndLockerBankInfoObject);
}

export const formatExpiryDate = (value) => {
    // Remove all non-digit characters
    const cleanedValue = value.replace(/\D/g, '');

    // Format the value as MM/YY
    if (cleanedValue.length <= 2) {
        return cleanedValue;
    } else if (cleanedValue.length <= 4) {
        return `${cleanedValue.slice(0, 2)}/${cleanedValue.slice(2)}`;
    } else {
        return `${cleanedValue.slice(0, 2)}/${cleanedValue.slice(2, 4)}`;
    }
};

export const handleStoreUserDataInLocalStorage = (userInfo) => {
    storeSecureItemInSpecifiedStorage(LOCAL_STORAGE, IS_LOGGED_IN, true);
    storeSecureItemInSpecifiedStorage(LOCAL_STORAGE, USER_INFO, userInfo);
    storeSecureItemInSpecifiedStorage(LOCAL_STORAGE, ACCESS_TOKEN, userInfo?.access);
    storeSecureItemInSpecifiedStorage(LOCAL_STORAGE, REFRESH_TOKEN, userInfo?.refresh);
    storeSecureItemInSpecifiedStorage(LOCAL_STORAGE, ACCESS_TOKEN_EXPIRY_TIME, convertSecondsToDateTimeFormat(userInfo?.access_expires_in));
    storeSecureItemInSpecifiedStorage(LOCAL_STORAGE, REFRESH_TOKEN_EXPIRY_TIME, convertSecondsToDateTimeFormat(userInfo?.refresh_expires_in));
}

export const calculateTimeDifference = (rentalTime, returnFormat = 'object') => {
    const rentalDate = moment(rentalTime);
    const currentDate = moment();
    const duration = moment.duration(currentDate.diff(rentalDate));

    const hours = Math.floor(duration.asHours());
    const minutes = Math.floor(duration.minutes());
    const seconds = Math.floor(duration.seconds());

    if (returnFormat === 'elapsed') {
        const elapsedTime = hours > 0 ? `${hours}.${minutes}.${seconds.toString().padStart(2, '0')}` : `${minutes}.${seconds.toString().padStart(2, '0')}`;
        return elapsedTime;
    }

    // Default: Return as an object
    return { hours, minutes, seconds };
};


// export const calculateTimeDifference = (hours,minutes,seconds) => {
//     const elapsedTime = `${hours > 0 ? hours : ""}${hours > 0 ? "." : ""}${minutes.toString().padStart(2, '0')}.${seconds.toString().padStart(2, '0')}`;
//     return elapsedTime;
// }

// get min and max price objects and construct price-in-words strings to show on UI
export const getMinAndMaxPriceObjects = (selectedEquipment) => {
    // Assuming pricing duration is uniform for all: hours
    let minRateObject = selectedEquipment?.pricing_info[0];
    let maxRateObject = selectedEquipment?.pricing_info[0];
    
    selectedEquipment?.pricing_info.forEach((pricingObject) => {
        if (pricingObject.rent_price < minRateObject.rent_price) {
            minRateObject = pricingObject;
        }
        else if (pricingObject.rent_price > maxRateObject.rent_price) {
            maxRateObject = pricingObject;
        }
    });
    
    minRateObject.pricePerDurationInWords = `${minRateObject.currency} ${minRateObject.rent_price} / ${minRateObject.rent_minute} ${minRateObject.duration}`;
    maxRateObject.pricePerDurationInWords = `${maxRateObject.rent_minute} ${maxRateObject.duration} rate: ${maxRateObject.currency} ${maxRateObject.rent_price}`;
    
    return {
        min: minRateObject,
        max: maxRateObject
    };
};

export const calculateRentForProduct = (selectedEquipment) => {
    console.log("selectedEquipment",selectedEquipment)
    // Assuming pricing duration is uniform for all: hours
    let minRateObject = selectedEquipment?.pricing_info[0];
    let maxRateObject = selectedEquipment?.pricing_info[0];
    
    selectedEquipment?.pricing_info.forEach((pricingObject) => {
        if (pricingObject.rent_price < minRateObject.rent_price) {
            minRateObject = pricingObject;
        }
        else if (pricingObject.rent_price > maxRateObject.rent_price) {
            maxRateObject = pricingObject;
        }
    });
    
    minRateObject.pricePerDurationInWords = `${minRateObject.currency} ${minRateObject.rent_price} / ${minRateObject.rent_minute} ${minRateObject.duration}`;
    maxRateObject.pricePerDurationInWords = `${maxRateObject.rent_minute} ${maxRateObject.duration} rate: ${maxRateObject.currency} ${maxRateObject.rent_price}`;
    
    return {
        min: minRateObject,
        max: maxRateObject
    };
};

export const formatTimeInLeadingDigits = (value) => {
        return String(value).padStart(2, '0'); // Pads single digits with a leading zero
}

export const scrollSmoothlyToPageTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    return;
}

export const arePaymentGatewayAndAccessKeyValid = (paymentGateway, accessKey) => {
    const isPaymentGatewayValid = /razorpay/i.test(paymentGateway);
    const isAccessKeyValid = /order_.*/.test(accessKey);

    return (isPaymentGatewayValid && isAccessKeyValid);
};

const degreesToRadians = (degrees) => degrees * (Math.PI / 180);

export const calculateDistance = (coord1, coord2) => {
    // console.log('lat1, lng1: ', typeof coord1.latitude, typeof coord1.longitude);
    // console.log('2: ', typeof coord2.latitude, typeof coord2.longitude);
    // console.log('coord1, coord2: ', coord1, coord2);
    const earthRadiusInMeters = 6371000;
    const lat1Rad = degreesToRadians(coord1.latitude);
    const lat2Rad = degreesToRadians(coord2.latitude);
    const latDiffRad = degreesToRadians(coord2.latitude - coord1.latitude);
    const lngDiffRad = degreesToRadians(coord2.longitude - coord1.longitude);

    const a =
        Math.sin(latDiffRad / 2) * Math.sin(latDiffRad / 2) +
        Math.cos(lat1Rad) * Math.cos(lat2Rad) *
        Math.sin(lngDiffRad / 2) * Math.sin(lngDiffRad / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distanceInMeters = earthRadiusInMeters * c;

    return distanceInMeters;
};

// export const isUserWithinAllowedDistanceOfALocker = (userCoordinates, lockerCoordinates, maxAllowedDistanceInMeters = 100) => {
//     if (!lockerCoordinates || !userCoordinates) {
//         return false;
//     }

//     const distanceInMeters = calculateDistance(userCoordinates, lockerCoordinates);

//     return distanceInMeters <= maxAllowedDistanceInMeters;
// };

export const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
            reject(new Error('Geolocation is not supported by your browser'));
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                resolve({
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude
                });
            },
            (error) => {
                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        reject(new Error('Location permission denied'));
                        break;
                    case error.POSITION_UNAVAILABLE:
                        reject(new Error('Location information unavailable'));
                        break;
                    case error.TIMEOUT:
                        reject(new Error('Location request timed out'));
                        break;
                    default:
                        reject(new Error('Unknown error getting location'));
                }
            },
            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 5000
            }
        );
    });
};

export const verifyUserProximity = async (lockerCoordinates, maxAllowedDistanceInMeters) => {
    try {
        const userLocation = await getCurrentLocation();
        console.log('user location: ', userLocation);
        console.log('locker coordinates: ', lockerCoordinates);

        const distanceInMeters = calculateDistance(userLocation, lockerCoordinates);

        const isNearby = distanceInMeters <= maxAllowedDistanceInMeters;

        return {
            isNearby,
            distance: Math.round(distanceInMeters),
            message: isNearby
                ? 'User is within allowed distance'
                : `User is ${Math.round(distanceInMeters)} meters away. Must be within ${maxAllowedDistanceInMeters} meters to open locker.`
        };
    } catch (error) {
        return {
            isNearby: false,
            distance: null,
            message: error.message || 'Failed to verify location proximity'
        };
    }
};

/**
 * Allows only up to 4 numeric digits.
 * @param {string} value - The input value to sanitize.
 * @returns {string} - A numeric-only string with max 4 digits.
 */
export const allowOnlyFourDigits = (value = "") => {
    return value.replace(/[^0-9]/g, "").slice(0, 4);
};

/**
 * Allows only numeric input (0–9).
 * @param {string} value - The input value to sanitize.
 * @returns {string} - A numeric-only string.
 */
export const allowOnlyNumbers = (value = "") => value.replace(/[^0-9]/g, "");

export const GetAvailableLockerSizesAndRedirectUser = async (
        lockerBankInfo,lockerBankId,userId,isAssignmentPricingEnabled,history,
        setLoaderDisabled = () => {},                    
        setLoadingMessage = () => {},                     
        setUserProximityError = () => {},                 
        setIsUserProximityErrorModalVisible = () => {} 
    ) => {
        const LoadersetON = () => {
            setLoaderDisabled(true);
        };
        const LoadersetOFF = () => {
            setLoaderDisabled(false);
        };
        const settings = getSecureItemFromSpecificStorage(LOCAL_STORAGE, LS_TENANT_ASSIGNMENT_LOCKER_BANK_SETTINGS);
        try {
            const response = await getAvailableLockerSizesWithUnits(settings?.slug, lockerBankId);
            if (response.status === SUCCESS) {
                const isManualLockerSelectionEnabled = !(settings?.usecase_settings?.random_selection_of_locker?.value === true);
                if (response.data.length === 1 && ( !isAssignmentPricingEnabled )) {
                    if(settings?.usecase_settings?.show_drop_btn_pwa?.value){
                        history.push({
                            pathname: getPathForNextPage(VERIFY_OTP, PARCEL_OWNER),
                            state: {
                                lockerSize: response.data[0].code,
                                lockerItem:response.data[0],
                            }
                        })
                    return;
                    }
                    if (isManualLockerSelectionEnabled && (! settings?.usecase_settings?.show_drop_btn_pwa?.value) ) {
                        history.push({
                            pathname: getPathForNextPage(HOME, LOCKER_UNIT_SELECTION),
                            state: {
                                lockerBankId,
                                assignmentSlug: settings?.slug,
                                userId,
                                selectedLockerSize: response.data[0]
                            }
                        });
                    }
                    else {
                            setLoadingMessage(i18n.t('reservingLocker'));
                            LoadersetON();
                        try {
                            if (settings?.is_geofencing) {
                                const lockerBankLatitude = lockerBankInfo?.latitude ? parseFloat(lockerBankInfo?.latitude) : null;
                                const lockerBankLongitude = lockerBankInfo?.longitude ? parseFloat(lockerBankInfo?.longitude) : null;
                                const maxAllowedDistanceInMeters = settings?.geofencing_radius;
                                const isUserNearbyLockerBank = await verifyUserProximity({latitude: lockerBankLatitude, longitude: lockerBankLongitude}, maxAllowedDistanceInMeters);
                    
                                if ( !isUserNearbyLockerBank.isNearby ) {
                                    setLoadingMessage('');
                                    setLoaderDisabled(false);
                                    setUserProximityError(isUserNearbyLockerBank.message);
                                    setIsUserProximityErrorModalVisible(true);
                    
                                    return;
                                }
                            }
                            const reserveLockerResponse = await reserveLocker(settings?.slug, lockerBankId, userId, response.data[0].code);
                            if (reserveLockerResponse.status === SUCCESS) {
                                const lockerUnitDetails = {
                                    ref_id: reserveLockerResponse?.data?.transaction_id,
                                    is_already_opened_once: false,
                                    locker_bank: {
                                        name: reserveLockerResponse?.data?.locker_bank_name
                                    },
                                    locker_door: {
                                        door_no: reserveLockerResponse?.data?.door_no
                                    }
                                };
                                LoadersetOFF();
                                history.push({
                                    pathname: getPathForNextPage(HOME, OPEN_LOCKER),
                                    state: {
                                        lockerUnitDetails
                                    }
                                });
                            }
                        } catch (error) {
                            LoadersetOFF();
                            console.error('Error while reserving locker: ', error);
                        } finally {
                            LoadersetOFF();
                            setLoadingMessage('');
                            
                        }
                        
                    }
                }
                else {
                    history.push({
                        pathname: getPathForNextPage(HOME, LOCKER_SIZE_SELECTION),
                        state: {
                            lockerBankId,
                            assignmentSlug: settings?.slug,
                            userId,
                            availableLockerSizes: response.data,
                            isManualLockerSelectionEnabled
                        }
                    });
                }
            }
        } catch (error) {
            console.error('Error while fetching available locker sizes: ', error);
        }
    }
export const getDotCount = (value) => (value.match(REGEXP_DOT) || []).length;


export const LanguageSelector = ({ width = 150 }) => {
	if (!global.selectedLanguage) {
		const saved = JSON.parse(localStorage.getItem(LS_SELECTED_LANGUAGE));
		global.selectedLanguage = saved || DEFAULT_LANGUAGE_ENGLISH;
		localStorage.setItem(
		LS_SELECTED_LANGUAGE,
		JSON.stringify(global.selectedLanguage)
		);
		i18n.changeLanguage(global.selectedLanguage.value);
	}
	const handleLanguageChange = (e) => {
		const selected = LANGUAGE_OPTIONS.find(
		(lang) => lang.value === e.target.value
		);
		localStorage.setItem(LS_SELECTED_LANGUAGE, JSON.stringify(selected));
        if(localStorage.getItem(LS_LOCAL_LANGUAGE)){
            localStorage.removeItem(LS_LOCAL_LANGUAGE);
                    
        }
		global.selectedLanguage = selected;
		i18n.changeLanguage(selected.value);
	};
    if (LANGUAGE_OPTIONS.length === 0) {
        applyBackendLanguages(getSecureItemFromSpecificStorage(LOCAL_STORAGE,LS_ALLOWED_LANGUAGE));
    }

	return (
		<div className="language-selector-wrapper">
			<select
				value={global.selectedLanguage.value}
				onChange={handleLanguageChange}
				className="language-selector-native"
				style={{
				width: width,
				height: 40,
				borderRadius: 6,
				padding: "0 10px",
				fontSize: 14,
				}}
			>
				{LANGUAGE_OPTIONS.map((lang) => (
				<option key={lang.value} value={lang.value}>
					{lang.label}
				</option>
				))}
			</select>
		</div>
	);
};

export const applyBackendLanguages = (backendAllowed) => {
	let allowed = [];
	if (Array.isArray(backendAllowed)) {
		allowed = backendAllowed.map((code) => code.trim());
	} else if (typeof backendAllowed === "string" && backendAllowed.trim() !== "") {
		allowed = backendAllowed.split(",").map((code) => code.trim());
	}
	try {
		let other = allowed.map((code) => {
		return {
			value: code,
			label: LANGUAGE_LABELS[code] || code.toUpperCase(),
			isRtl: false,
		};
		});
		// Update LANGUAGE_OPTIONS safely
		LANGUAGE_OPTIONS.length = 0;
		LANGUAGE_OPTIONS.push(...other);
	} catch (err) {
		console.error("Error in updating languages:", err);
	}
};

export const toFeetInches = (mm) => {
    if (!mm || isNaN(Number(mm))) return "-";
    const inches = parseFloat(mm) / 25.4;
    const ft = Math.floor(inches / 12);
    const inch = Math.round(inches % 12);
    return `${ft}ft ${inch}in`;
};

export const applyBackendLanguageConfig = (response)=>{
    if (!response?.data?.locker_bank) return;
    const locker = response.data.locker_bank;
    const defaultLanguage  = locker.language;
    const allowedLanguages = locker.allowed_languages;
    const localLanguage    = locker.local_language;
    if (localLanguage) {
        localStorage.setItem(LS_LOCAL_LANGUAGE, localLanguage);
    }
    applyBackendLanguages(allowedLanguages);
    if (defaultLanguage) {
        const label = LANGUAGE_LABELS[defaultLanguage];
        const backendLanguageObj = {
        value: defaultLanguage,
        label: label || defaultLanguage.toUpperCase(),
        isRtl: false,
        };
        localStorage.setItem(LS_SELECTED_LANGUAGE, JSON.stringify(backendLanguageObj));
        storeSecureItemInSpecifiedStorage(
        LOCAL_STORAGE,
        LS_ALLOWED_LANGUAGE,
        allowedLanguages
        );
        initializeI18nAndThenAppendCustomerSpecificStylesheet();
    }
}
export const buildInvoiceList = (lockers = []) => {
    return (lockers || []).flatMap((item) =>
        (item?.payment_records || []).map((payment) => ({
        // one invoice per payment
        invoice_no: payment.transaction_id || "-",
        locker_bank: item?.locker_bank?.name || "-",
        location: item?.locker_bank?.location?.name || "-",
        customer_id: item?.drop_user?.mobile_number ||
            item?.user?.mobile_number || "-",
        payment_id: payment.payment_id || payment.transaction_id || "-",
        payment_method:payment.payment_invoice_data?.payment_method ||
            payment.payment_type || "-",
        currency: payment.currency || "INR",
        amount: payment.amount,
        date: moment(payment.created_at).format("YYYY-MM-DD"),
        time: moment(payment.created_at).format("hh:mm:ss A"),
        }))
    );
};

export function decryptObjectDeep (obj) {
    if (Array.isArray(obj)) {
        return obj.map(decryptObjectDeep);
    }

    if (obj !== null && typeof obj === "object") {
        const result = {};
        Object.entries(obj).forEach(([key, value]) => {
        const decryptedKey = decryptKey(key) || key;
        result[decryptedKey] = decryptObjectDeep(value);
        });
        return result;
    }

    return obj;
}

export function base64UrlToBase64(str) {
    return str
        .replace(/-/g, "+")
        .replace(/_/g, "/")
        .padEnd(str.length + (4 - (str.length % 4)) % 4, "=");
}

export function isProbablyEncrypted(str) {
    return (
        typeof str === "string" &&
        str.length > MIN_ENCRYPTED_LENGTH &&
        BASE64_URL_CHARS_REGEX.test(str)
    );
}

export function decryptKey(encryptedKey) {
    if (!isProbablyEncrypted(encryptedKey)) {
        return encryptedKey;
    }

    const key = CryptoJS.SHA256(process.env.REACT_APP_CRYPTO_SECRET);
    const iv = CryptoJS.enc.Base64.parse(process.env.REACT_APP_CRYPTO_IV);

    try {
        const fixedBase64 = base64UrlToBase64(encryptedKey);
        const decrypted = CryptoJS.AES.decrypt(
        fixedBase64,
        key,
        {
            iv,
            mode: CryptoJS.mode.CBC,
            padding: CryptoJS.pad.Pkcs7,
        }
        );

        const result = decrypted.toString(CryptoJS.enc.Utf8);

        return result || encryptedKey; // fallback safety
    } catch {
        return encryptedKey;
    }
}


export function RenderTabs({
    isMobileOtpEnabled= false,
    isEmailOtpEnabled= false,
    isFirstTab=false,
    isSecondTab= false,
    FirstTabLabalName = "firstLabal",
    SecondTabLabalName = "SecondLabal",
    tabValue,
    onChange,
    t}) {
  const tabs = [];

  if (isMobileOtpEnabled || isFirstTab) {
    if(isMobileOtpEnabled){
        tabs.push({
            label: t("phoneNumberLabel"),
            value: 0
        });
    }else{
        tabs.push({
            label: t(FirstTabLabalName),
            value: 0
        });
    }
   
  }

  if (isEmailOtpEnabled || isSecondTab) {
    if(isEmailOtpEnabled){
        tabs.push({
        label: t("emailAddressLabel"),
        value: 1
        });
    }else{
        tabs.push({
        label: t(SecondTabLabalName),
        value: 1
        });
    }
    
  }

  return (
    <div className="tab-bar">
      {tabs.map((tab) => (
        <button
          key={tab.value}
          className={`tab-bar-label ${tabValue === tab.value ? "active" : ""}`}
          onClick={() => onChange(tab.value)}
        >
          {tab.label}
        </button>
      ))}

      {/* Indicator */}
      <div
        className="indicator"
        style={{
          width: `${100 / tabs.length}%`,
          transform: `translateX(${tabValue * 100}%)`
        }}
      />
    </div>
  );
};

export const getAssignedLockers = async (assignmentId, searchText = '', pageNumber = 1) => {
            try {
                const getAssignedLockerListResponse = await getAssignedLockerList(assignmentId, searchText, pageNumber);
    
                if (getAssignedLockerListResponse?.status === SUCCESS) {
                    return getAssignedLockerListResponse;
                }
            } catch (error) {
                console.error('Error while fetching assigned lockers list: ', error);
                return [];
            }
};



/**
 * Transform API locker data to component expected format
 * @param {Array} apiData - Raw data from API
 * @returns {Array} - Transformed data for components
 */
export const transformLockerData = (apiData) => {
  if (!apiData || !Array.isArray(apiData)) {
    return [];
  }

  return apiData.map((item, index) => {
    // Generate locker number based on available data
    const generateLockerNumber = () => {
      if (item.lockerNumber) return item.lockerNumber;
      const prefix = (item.city || item.state || 'LKR').substring(0, 2).toUpperCase();
      return `${prefix}-${String(item.id || index + 1).padStart(3, '0')}`;
    };

    // Determine type based on available data
    const determineType = () => {
      if (item.category?.name) return item.category.name.toLowerCase();
      if (item.name?.toLowerCase().includes('city')) return 'city';
      if (item.name?.toLowerCase().includes('industrial')) return 'industrial';
      if (item.name?.toLowerCase().includes('commercial')) return 'commercial';
      if (item.name?.toLowerCase().includes('temple')) return 'temple';
      if (item.name?.toLowerCase().includes('tourist')) return 'tourist';
      return 'city'; // default
    };

    // Build complete address
    const buildAddress = () => {
      const parts = [
        item.address_line1,
        item.address_line2,
        item.city,
        item.state,
        item.pincode
      ].filter(Boolean);
      return parts.length > 0 ? parts.join(', ') : 'Address not available';
    };

    // Calculate availability based on status
    const getAvailability = () => {
      if (item.status === 'published') return 10;
      if (item.status === 'draft') return 5;
      return 8; // default
    };

    return {
      // Core fields with defaults
      id: item.id || index + 1,
      name: item.name || item.location || 'Unknown Location',
      marathiName: item.marathi_name || item.name || '', // If API provides Marathi name
      
      // Location fields
      district: item.city || item.state || 'Maharashtra',
      districtMarathi: item.city_marathi || item.city || 'महाराष्ट्र',
      position: { 
        lat: item.latitude ? parseFloat(item.latitude) : 18.5204, // Default Pune lat
        lng: item.longitude ? parseFloat(item.longitude) : 73.8567 // Default Pune lng
      },
      
      // Description and type
      type: determineType(),
      description: item.description || item.location || 'No description available',
      address: buildAddress(),
      
      // Images
      svgFile: LockerIcon, // Default icon
      logoImage: item.logo_image || null,
      backgroundImage: item.background_image || null,
      
      // Locker details
      lockerNumber: generateLockerNumber(),
      distance: '0', // Will be calculated by map component
      available: getAvailability(),
      price: item.price || '₹30/hr', // Default price
      
      // Status and metadata
      status: item.status || 'published',
      slug: item.slug || '',
      pincode: item.pincode || '',
      country: item.country || 'India',
      state: item.state || 'Maharashtra',
      city: item.city || '',
      
      // Keep original data for reference if needed
      originalData: { ...item }
    };
  });
};

/**
 * Calculate distance between two coordinates using Haversine formula
 * @param {Object} location1 - { lat, lng }
 * @param {Object} location2 - { lat, lng }
 * @returns {string} - Distance in km (1 decimal)
 */
export const calculateDistanceMap = (location1, location2) => {
  if (!location1 || !location2) return '0';
  
  const R = 6371; // Earth's radius in km
  const lat1 = location1.lat * Math.PI / 180;
  const lat2 = location2.lat * Math.PI / 180;
  const dLat = (location2.lat - location1.lat) * Math.PI / 180;
  const dLon = (location2.lng - location1.lng) * Math.PI / 180;

  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1) * Math.cos(lat2) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c;
  
  return distance.toFixed(1);
};

/**
 * Sort locations by distance from user
 * @param {Array} locations - Array of location objects
 * @param {Object} userLocation - { lat, lng }
 * @returns {Array} - Sorted locations
 */
export const sortByDistance = (locations, userLocation) => {
  if (!locations || !userLocation) return locations;
  
  return [...locations].sort((a, b) => 
    parseFloat(a.distance) - parseFloat(b.distance)
  );
};


/**
 * Generates 30-minute time slots for a given date.
 *
 * Behavior:
 * 1. If the selected date is **today**, time slots start from the **next available half-hour**
 *    after the current time (or fromDate if provided).
 * 2. If the selected date is **not today** (future/custom date), time slots start from **12:00 AM**.
 * 3. Time slots are generated every **30 minutes** until **11:30 PM** of that day.
 *
 * Example Output:
 * [
 *   "12:00 AM",
 *   "12:30 AM",
 *   "1:00 AM",
 *   ...
 *   "11:30 PM"
 * ]
 *
 * @param {Date} selectedDate - The date for which time slots should be generated.
 * @param {Date|null} fromDate - Optional reference time. If provided and the selected date
 *                               is today, slots will start from the next half-hour after this time.
 *
 * @returns {string[]} Array of formatted time slot strings (e.g., "2:30 PM").
 */
// export const generateTimeSlots = (selectedDate, fromDate = null) => {
//   // Return empty array if no date is provided
//   if (!selectedDate) return [];

//   const slots = [];
//   let startTime;

//   /**
//    * CASE 1: Selected date is today
//    * --------------------------------
//    * Start generating slots from the next available half-hour
//    * based on the current time (or provided fromDate).
//    */
//   if (isToday(selectedDate)) {
//     startTime = getNextHalfHourSlot(fromDate || new Date());

//     // Set end time to 11:30 PM of the selected day
//     const endOfDay = new Date(selectedDate);
//     endOfDay.setHours(23, 30, 0, 0);

//     let currentSlot = new Date(startTime);

//     // Generate slots until end of the day
//     while (currentSlot <= endOfDay) {
//       slots.push(format(currentSlot, "h:mm a"));
//       currentSlot = addMinutes(currentSlot, 30);
//     }
//   } 
//   /**
//    * CASE 2: Selected date is tomorrow or any future date
//    * -----------------------------------------------------
//    * Start generating slots from 12:00 AM and cover the full day.
//    */
//   else {
//     startTime = startOfDay(selectedDate);

//     const endOfDay = new Date(selectedDate);
//     endOfDay.setHours(23, 30, 0, 0);

//     let currentSlot = new Date(startTime);

//     while (currentSlot <= endOfDay) {
//       slots.push(format(currentSlot, "h:mm a"));
//       currentSlot = addMinutes(currentSlot, 30);
//     }
//   }

//   return slots;
// };

export const getSSize24HrPrice = (pricing = []) => {
  const TARGET_DURATION = 86400; // 24 hours in seconds

  // Filter S size lockers
  const sSizeItems = pricing.filter(
    (item) => item.locker_size__code === "S"
  );

  if (!sSizeItems.length) return null;

  // Pick best base (largest duration for better accuracy)
  const baseItem = sSizeItems.reduce((prev, curr) =>
    parseFloat(curr.duration) > parseFloat(prev.duration) ? curr : prev
  );

  const baseDuration = parseFloat(baseItem.duration);
  const basePrice = baseItem.price;

  if (!baseDuration || !basePrice) return null;

  // Calculate proportional price
  const pricePerSecond = basePrice / baseDuration;
  const price24Hr = pricePerSecond * TARGET_DURATION;

  return Math.round(price24Hr); // or use toFixed(2) if needed
};

// export const getPriceFor24Hr = (pricing = []) => {
//   const TARGET_DURATION = 86400; // 24h

//   if (!pricing || pricing.length === 0) return null;

//   // pick largest duration (important for correct pricing)
//   const baseItem = pricing.reduce((prev, curr) =>
//     parseFloat(curr.duration) > parseFloat(prev.duration) ? curr : prev
//   );

//   const duration = parseFloat(baseItem.duration);
//   const price = baseItem.price;

//   if (!duration || !price) return null;

//   const pricePerSecond = price / duration;
//   const finalPrice = pricePerSecond * TARGET_DURATION;

//   return Math.round(finalPrice);
// };


export const getPriceFor24Hr = (pricing = []) => {
  const TARGET_DURATION = 86400; // 24h

  if (!pricing || pricing.length === 0) return null;

  // First, try to find exact 24-hour duration
  const exactMatch = pricing.find(item => 
    parseFloat(item.duration) === TARGET_DURATION
  );

  let baseItem;
  if (exactMatch) {
    // Use exact 24-hour pricing if available
    baseItem = exactMatch;
  } else {
    // Otherwise, pick largest duration
    baseItem = pricing.reduce((prev, curr) =>
      parseFloat(curr.duration) > parseFloat(prev.duration) ? curr : prev
    );
  }

  const duration = parseFloat(baseItem.duration);
  const price = baseItem.price;

  if (!duration || !price) return null;

  // If we already have exact 24-hour price, just return it (no calculation needed)
  if (exactMatch && duration === TARGET_DURATION) {
    return Math.round(price);
  }

  // Otherwise calculate prorated price
  const pricePerSecond = price / duration;
  const finalPrice = pricePerSecond * TARGET_DURATION;

  return Math.round(finalPrice);
};

export const getSlotIdFor24Hourse = (sizePricing = []) => {
  const TARGET_DURATION = 86400; // 24h in seconds

  if (!sizePricing || sizePricing.length === 0) return null;

  // First, try to find exact 24-hour duration
  const exactMatch = sizePricing.find(item => 
    parseFloat(item.duration) === TARGET_DURATION
  );

  if (exactMatch) {
    return exactMatch.id;
  }

  // If no exact match, find the largest duration
  const largestDuration = sizePricing.reduce((prev, curr) =>
    parseFloat(curr.duration) > parseFloat(prev.duration) ? curr : prev
  );

  return largestDuration?.id || null;
};



// utils/lockerUtils.js

/**
 * Check if a reservation is currently active (current time between check-in and check-out)
 * @param {string|Date} reserveStart - Reservation start date/time
 * @param {string|Date} reserveEnd - Reservation end date/time
 * @returns {boolean} - True if reservation is currently active
 */
export const isActiveReservation = (reserveStart, reserveEnd) => {
  const now = new Date();
  const start = new Date(reserveStart);
  const end = new Date(reserveEnd);
  return now >= start && now <= end;
};

/**
 * Check if a reservation is upcoming (future)
 * @param {string|Date} reserveStart - Reservation start date/time
 * @returns {boolean} - True if reservation is upcoming
 */
export const isUpcomingReservation = (reserveStart) => {
  const now = new Date();
  const start = new Date(reserveStart);
  return start > now;
};

/**
 * Check if a reservation is expired (past)
 * @param {string|Date} reserveEnd - Reservation end date/time
 * @returns {boolean} - True if reservation is expired
 */
export const isExpiredReservation = (reserveEnd) => {
  const now = new Date();
  const end = new Date(reserveEnd);
  return end < now;
};

/**
 * Get the current status of a reservation based on timing
 * @param {string|Date} reserveStart - Reservation start date/time
 * @param {string|Date} reserveEnd - Reservation end date/time
 * @returns {string} - 'active', 'upcoming', or 'expired'
 */
export const getReservationStatus = (reserveStart, reserveEnd) => {
  if (isActiveReservation(reserveStart, reserveEnd)) return "active";
  if (isUpcomingReservation(reserveStart)) return "upcoming";
  if (isExpiredReservation(reserveEnd)) return "expired";
  return "upcoming";
};

/**
 * Format date and time to a readable string
 * @param {string|Date} dateString - Date to format
 * @param {boolean} includeTime - Whether to include time in the output
 * @returns {string} - Formatted date string
 */
export const formatDateTime = (dateString, includeTime = true) => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    if (includeTime) {
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }).replace(',', ' at');
    } else {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    }
  } catch (error) {
    return dateString;
  }
};

/**
 * Format date only (no time)
 * @param {string|Date} dateString - Date to format
 * @returns {string} - Formatted date string (YYYY-MM-DD)
 */
export const formatDate = (dateString) => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  } catch (error) {
    return dateString;
  }
};

/**
 * Parse a check-in date string that could be in "DD/MM/YYYY at HH:MM" or ISO format.
 * @param {string|Date} dateValue - Date value to parse
 * @returns {Date|null} - Parsed Date or null if invalid
 */
export const parseCheckInDateTime = (dateValue) => {
  if (!dateValue) return null;

  if (dateValue instanceof Date) {
    return isNaN(dateValue) ? null : dateValue;
  }

  if (typeof dateValue !== "string") return null;

  if (dateValue.includes(" at ")) {
    const [datePart, timePart] = dateValue.split(" at ");
    if (!datePart || !timePart) return null;

    const [day, month, year] = datePart.split("/");
    const [hours, minutes] = timePart.split(":");

    const parsed = new Date(year, month - 1, day, hours, minutes);
    return isNaN(parsed) ? null : parsed;
  }

  const parsedISO = new Date(dateValue);
  return isNaN(parsedISO) ? null : parsedISO;
};

/**
 * Extract reservation start date from a locker object or raw date value.
 * @param {object|string|Date} lockerOrDate - Locker object or date value
 * @returns {Date|null} - Parsed Date or null if invalid
 */
export const getReservationStartDate = (lockerOrDate) => {
  if (lockerOrDate instanceof Date || typeof lockerOrDate === "string") {
    return parseCheckInDateTime(lockerOrDate);
  }

  const candidate =
    lockerOrDate?.rawCheckIn ||
    lockerOrDate?.reserve_start ||
    lockerOrDate?.checkIn;

  return parseCheckInDateTime(candidate);
};

/**
 * Get hours remaining until reservation start.
 * @param {object|string|Date} lockerOrDate - Locker object or date value
 * @param {Date} now - Current time override
 * @returns {number|null} - Hours until start or null if invalid
 */
export const getHoursUntilReservationStart = (lockerOrDate, now = new Date()) => {
  const reserveStart = getReservationStartDate(lockerOrDate);
  if (!reserveStart) return null;
  return (reserveStart - now) / (1000 * 60 * 60);
};

/**
 * Check if cancellation is allowed based on minimum hours before check-in.
 * @param {object|string|Date} lockerOrDate - Locker object or date value
 * @param {number} minHours - Minimum hours required
 * @returns {boolean} - True if allowed
 */
export const isCancellationAllowed = (lockerOrDate, minHours = 3) => {
  const diffHours = getHoursUntilReservationStart(lockerOrDate);
  if (diffHours === null) return false;
  return diffHours >= minHours;
};

/**
 * Check if modification is allowed based on minimum hours before check-in.
 * @param {object|string|Date} lockerOrDate - Locker object or date value
 * @param {number} minHours - Minimum hours required
 * @returns {boolean} - True if allowed
 */
export const isModificationAllowed = (lockerOrDate, minHours = 3) => {
  return isCancellationAllowed(lockerOrDate, minHours);
};

/**
 * Get status style configuration
 * @param {string} status - Reservation status ('active', 'upcoming', 'expired')
 * @returns {object} - Style configuration with className and text
 */
export const getStatusStyle = (status) => {
  switch(status) {
    case "active":
      return {
        className: "status active-status",
        text: "Active",
        color: "#4caf50",
        backgroundColor: "#e8f5e9"
      };
    case "upcoming":
      return {
        className: "status upcoming-status",
        text: "Upcoming",
        color: "#ff9800",
        backgroundColor: "#fff3e0"
      };
    case "expired":
      return {
        className: "status expired-status",
        text: "Expired",
        color: "#9e9e9e",
        backgroundColor: "#f5f5f5"
      };
    default:
      return {
        className: "status upcoming-status",
        text: "Upcoming",
        color: "#ff9800",
        backgroundColor: "#fff3e0"
      };
  }
};

/**
 * Get button actions based on reservation status
 * @param {string} status - Reservation status
 * @returns {object} - Button configuration
 */
export const getButtonActions = (status) => {
  switch(status) {
    case "active":
      return {
        primary: { text: "Access Locker", className: "btn-primary" },
        secondary: { text: "Extend Booking", className: "btn-outline" }
      };
    case "upcoming":
      return {
        primary: null,
        secondary: { text: "Modify Booking", className: "btn-outline" },
        tertiary: { text: "Cancel Booking", className: "btn-outline" }
      };
    case "expired":
      return {
        disabled: { text: "Booking Expired", className: "btn-outline disabled", disabled: true }
      };
    default:
      return {
        secondary: { text: "Modify Booking", className: "btn-outline" },
        tertiary: { text: "Cancel Booking", className: "btn-outline" }
      };
  }
};

/**
 * Process a single locker item with all necessary formatting
 * @param {object} locker - Raw locker data
 * @returns {object} - Processed locker with formatted dates and status
 */
export const processLockerItem = (locker) => {
  const reserveStart = locker.rawCheckIn || locker.reserve_start || locker.checkIn;
  const reserveEnd = locker.rawCheckOut || locker.reserve_end || locker.checkOut;
  const status = getReservationStatus(reserveStart, reserveEnd);
  
  return {
    ...locker,
    status,
    formattedCheckIn: formatDateTime(reserveStart, true),
    formattedCheckOut: formatDateTime(reserveEnd, true),
    formattedFromDate: formatDate(reserveStart),
    formattedToDate: formatDate(reserveEnd),
    statusStyle: getStatusStyle(status),
    buttonActions: getButtonActions(status),
    isActive: status === "active",
    isUpcoming: status === "upcoming",
    isExpired: status === "expired"
  };
};

/**
 * Categorize lockers into upcoming, active, and history based on timing
 * @param {Array} lockers - Array of locker objects
 * @returns {object} - Categorized lockers { upcoming, active, history }
 */
export const categorizeLockers = (lockers) => {
  const upcoming = [];
  const active = [];
  const history = [];
  
  lockers.forEach(locker => {
    const reserveStart = locker.rawCheckIn || locker.reserve_start || locker.checkIn;
    const reserveEnd = locker.rawCheckOut || locker.reserve_end || locker.checkOut;
    const status = locker.status || getReservationStatus(reserveStart, reserveEnd);
    
    if (status === "reserved" || status === "upcoming") {
      if (isActiveReservation(reserveStart, reserveEnd)) {
        active.push({ ...locker, reservationType: "active" });
      } else if (isUpcomingReservation(reserveStart)) {
        upcoming.push({ ...locker, reservationType: "upcoming" });
      } else {
        history.push({ ...locker, reservationType: "expired" });
      }
    } else if (status === "completed" || status === "expired" || status === "cancelled") {
      history.push({ ...locker, reservationType: status });
    } else {
      upcoming.push({ ...locker, reservationType: "upcoming" });
    }
  });
  
  return { upcoming, active, history };
};


// Utility function to find matching slot by duration
export const findMatchingSlotByDuration = (locker, targetDurationInMs) => {
  if (!locker?.bookingSlots || !targetDurationInMs) return null;
  
  return locker.bookingSlots.find(slot => 
    parseFloat(slot.durationInMilliseconds) === targetDurationInMs
  ) || null;
};

// Utility function to get duration in milliseconds from a slot
export const getDurationInMs = (slot) => {
  return slot?.durationInMilliseconds || null;
};


// export const generateReserveTiming = (hours = 6) => {
//   const now = new Date();

//   const reserveStart = new Date(now);
//   const reserveEnd = new Date(now.getTime() + hours * 60 * 60 * 1000);

//   return {
//     reserve_start: reserveStart.toISOString(),
//     reserve_end: reserveEnd.toISOString(),
//   };
// };

export const generateReserveTiming = (hours = 6) => {
  const now = new Date();

  // ✅ Start after 3 minutes
  const reserveStart = new Date(now.getTime() + 3 * 60 * 1000);

  // ✅ End after (hours) from adjusted start
  const reserveEnd = new Date(
    reserveStart.getTime() + hours * 60 * 60 * 1000
  );

  return {
    reserve_start: reserveStart.toISOString(),
    reserve_end: reserveEnd.toISOString(),
  };
};

export const getRoundedDurationInHours = (start, end) => {
  if (!start || !end) return 0;

  const diffMs = new Date(end) - new Date(start);

  if (isNaN(diffMs) || diffMs <= 0) return 0;

  const hours = diffMs / (1000 * 60 * 60);

  // round to next 24-hour block
  const roundedHours = Math.ceil(hours / 24) * 24;

  return roundedHours;
};
export const getRoundedDurationInDays = (start, end) => {
  if (!start || !end) return 0;

  const diffMs = new Date(end) - new Date(start);

  if (isNaN(diffMs) || diffMs <= 0) return 0;

  const days = diffMs / (1000 * 60 * 60 * 24);

  // round to next full day
  const roundedDays = Math.ceil(days);

  return roundedDays;
};

export const getPriceForSelectedTiming = (label, price24h) => {
  if (!label || !price24h) return 0;

  const hours = Number(label);
  const basePrice = Number(price24h);

  if (isNaN(hours) || isNaN(basePrice) || hours <= 0) return 0;

  return (hours / 24) * basePrice;
};


export const handleBulkReservationPayment = async (reserveLockerResponse, history, options = {}) => {
    console.log("calling utils with reserveLockerResponse: ", reserveLockerResponse);

    try {
        const { isAdvanceBooking = false, bookingType } = options || {};
        const advanceBookingFlag = bookingType === ADVANCE_BOOKING || isAdvanceBooking;
        if (reserveLockerResponse?.data?.status !== "success") {
            console.error("Reservation failed:", reserveLockerResponse);
            return null;
        }
        const temp = reserveLockerResponse?.data?.data;
        const paymentPayload = {
        payment_gateway: temp?.assignment_payment_gateway?.payment_gateway,
        booking_id: temp?.ref_id,
        locker_bank_id: temp?.locker_bank?.id,
        amount: temp?.amount,
        accept_payment_mode: "default",
        };

        console.log("Payment Payload:", paymentPayload);
        const prepaymentResponse = await buildBulkPaymentInitial(paymentPayload);
        console.log("Payment Response:", prepaymentResponse);
        if (prepaymentResponse?.data?.status !== "success") {
        console.error("Payment init failed:", prepaymentResponse);
        return null;
        }
        const lockerUnitDetails = {
            ref_id: temp?.ref_id,
            is_already_opened_once: false,
            locker_bank: {
                name: temp?.locker_bank_name,
            },
            locker_door: {
                door_no: temp?.door_no,
            },
        };

        const lockerData = {
            reservedLocker: temp,
            locker_bank: {
                name: temp?.locker_bank_name,
                location: {
                name: temp?.locker_bank_location,
                },
            },
            assignment: {
                payment_gateway: [temp?.assignment_payment_gateway],
            },
            payment_records: [
                { amount: prepaymentResponse?.data?.data?.amount },
            ],
            user: {
                username: prepaymentResponse?.data?.data?.user?.username,
                email: prepaymentResponse?.data?.data?.user?.email,
                mobile_number:
                prepaymentResponse?.data?.data?.user?.mobile_number,
            },
        };

        storeSecureItemInSpecifiedStorage(
            SESSION_STORAGE,
            SS_PAYMENT_GATEWAY,
            temp?.assignment_payment_gateway?.payment_gateway
        );

        storeSecureItemInSpecifiedStorage(
            SESSION_STORAGE,
            SS_PAYMENT_GATEWAY_ACCESS_KEY,
            prepaymentResponse?.data?.data?.order_id
        );
        
        const currentPageName = getPageNameUsingPath(history?.location?.pathname);
        const redirectionPathname =
            (currentPageName ? getPathForNextPage(currentPageName, PAYMENT_GATEWAY) : undefined) ||
            getPathForNextPage(LOCKER_SIZE_SELECTION, PAYMENT_GATEWAY);

        history.replace({
            pathname: redirectionPathname,
            state: {
                lockerUnitDetails,
                isLockerReservedTillSuccessfulPayment: true,
                lockerData,
                currentReferenceId:
                prepaymentResponse?.data?.data?.order_id,
                bookingType: advanceBookingFlag ? ADVANCE_BOOKING : BULK_BOOKING
            },
        });

        return {
            success: true,
        };
    } catch (error) {
        console.error("Error in bulk payment flow:", error);
        return {
            success: false,
            error,
        };
    }
};


export const getCurrencySymbol = (code = "") => {
  if (!code) return "";
  const currency = currencies.find(
    (c) => c.code.toUpperCase() === code.toUpperCase()
  );
  return currency?.symbol || "";
};